import { Link } from "react-router-dom";
import { useApplyCta } from "@/features/members/hooks/useApplyCta";
import { Button } from "@/components/ui/button";

/**
 * Both ends of a preview: what it is at the top, what is behind the cut at
 * the bottom, and the way through it in either case.
 *
 * The directory and the resource library both show a capped sample to
 * somebody who is not a member, and both end the same way — the count of what
 * is withheld, and the one blue button on the page. Shared so the two cannot
 * come to disagree about what that offer says.
 *
 * The count comes from the server's real total rather than the cards on
 * screen, so it says what is actually being withheld.
 */
/**
 * The top of a preview: what this page is showing, before anything is scrolled.
 *
 * Needed because lifting the directory's cap removed the only offer on the
 * page. `PreviewCutoff` draws when something is withheld, and with every card
 * on screen nothing is, so the directory had no way to apply from it at all.
 *
 * It is also the one place where saying what the page shows earns its keep.
 * The house rule is not to narrate the obvious, and the cards are visibly
 * redacted — but a stranger meeting 390 nameless cards does not know whether
 * the names are missing or withheld, and Chelsea asked for the difference to
 * be stated. Once, at the top, and not again at the foot.
 */
export function PreviewBanner({
  show,
  children,
}: {
  show: boolean;
  children: React.ReactNode;
}) {
  const label = useApplyCta();
  if (!show) return null;
  return (
    <div className="animate-page-enter mt-6 flex flex-col gap-3 rounded-lg border px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-muted-foreground text-sm">{children}</p>
      <Button asChild variant="brand" className="shrink-0">
        <Link to="/apply">{label}</Link>
      </Button>
    </div>
  );
}

export function PreviewCutoff({
  hidden,
  show,
  noun = "members",
  audience = "full members",
}: {
  hidden: number;
  show: boolean;
  noun?: string;
  /** Who the rest is for. The directory's detail is a paid-tier benefit; the
   *  library is promised to every member, free ones included. */
  audience?: string;
}) {
  const label = useApplyCta();
  if (!show || hidden <= 0) return null;
  return (
    <div className="animate-page-enter mt-10 flex flex-col items-center gap-3 border-t pt-10 text-center">
      <p className="text-muted-foreground text-sm">
        {hidden} more {hidden === 1 ? noun.replace(/s$/, "") : noun} are
        available to {audience}.
      </p>
      <Button asChild variant="brand">
        <Link to="/apply">{label}</Link>
      </Button>
    </div>
  );
}

export default PreviewCutoff;
